import React, { useState, useEffect, useRef } from "react";
import MainLayout from "../../components/Layouts/Main/MainLayout.index";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaTrash,
  FaDownload,
  FaImage,
  FaArrowUp,
  FaArrowDown,
  FaEye,
  FaCloudUploadAlt,
} from "react-icons/fa";
import {
  parseBannersFromHtml,
  generateBannerHtml,
} from "../../utils/bannerUtils";
import {
  uploadBannerImage,
  previewPublishBannerHtml,
  publishBannerHtml,
  getBannerHtml,
} from "../../api/project";
import { toast } from "react-toastify";
import {
  BannerManageLayout,
  BannerManageBody,
  Title,
  StyledCard,
  CardHeader,
  CardBody,
  TableWrapper,
  StyledTable,
  PreviewImage,
  FormControl,
  SmallButton,
  UploadButton,
  ModalOverlay,
  ModalContent,
  Instructions,
  FlexRow,
  TextMuted,
} from "./BannerManage.style";

export default function BannerManage() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [uploadedImages, setUploadedImages] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentEditingIndex, setCurrentEditingIndex] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const userId = sessionStorage.getItem("userId");
    if (!userId) {
      navigate("/");
      return;
    }
    loadBannersFromServer();
  }, [navigate]);

  const loadBannersFromServer = async () => {
    setIsLoading(true);
    try {
      const response = await getBannerHtml();
      if (response.msg) {
        const parsed = parseBannersFromHtml(response.msg);
        setBanners(parsed);
        toast.success(`${parsed.length}件のバナーを読み込みました`);
      } else {
        toast.error("バナーHTMLを取得できませんでした");
      }
    } catch (error) {
      console.error("Failed to load banners:", error);
      toast.error("バナーの読み込みに失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || currentEditingIndex === null) return;

    if (!file.type.startsWith("image/")) {
      toast.error("画像ファイルを選択してください");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("bannerIndex", currentEditingIndex);

      const response = await uploadBannerImage(formData);

      const imageUrl = response.data || response.url;
      const fileName = imageUrl.split("/").pop();

      setUploadedImages((prev) => ({
        ...prev,
        [currentEditingIndex]: {
          name: fileName,
          fullUrl: imageUrl,
          uploaded: true,
        },
      }));

      updateBanner(currentEditingIndex, "src", `${imageUrl}`);

      toast.success(`画像「${file.name}」をアップロードしました`);
      setShowUploadModal(false);
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("アップロードに失敗しました");
    } finally {
      setIsUploading(false);
    }
  };

  const openUploadModal = (index) => {
    setCurrentEditingIndex(index);
    setShowUploadModal(true);
  };

  const updateBanner = (index, field, value) => {
    setBanners((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addBanner = () => {
    const newIndex = banners.length;
    const newBanner = {
      id: `big_${String(newIndex + 1).padStart(2, "0")}_new`,
      index: newIndex,
      href: "https://example.com",
      src: "pic/new_banner.jpg",
      alt: `バナー${newIndex + 1}`,
    };
    setBanners([...banners, newBanner]);
    toast.info("新しいバナーを追加しました");
  };

  const removeBanner = (index) => {
    if (banners.length <= 1) {
      toast.warning("最後のバナーは削除できません");
      return;
    }
    setBanners(banners.filter((_, i) => i !== index));
    setUploadedImages((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
    toast.info("バナーを削除しました");
  };

  const moveBanner = (index, direction) => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === banners.length - 1) return;

    setBanners((prev) => {
      const updated = [...prev];
      const newIndex = direction === "up" ? index - 1 : index + 1;
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
      return updated;
    });
  };

  const previewForPublish = async () => {
    try {
      const htmlContent = generateBannerHtml(banners);

      await previewPublishBannerHtml({
        html: htmlContent,
        banners: banners,
      });
      const url = `${process.env.REACT_APP_DOMAIN}/jankatsumaster_banner/tmp_banners.html?ts=${Date.now()}`;
      window.open(url, "_blank");
    } catch (error) {
      console.error("プレビュー failed:", error);
      toast.error(
        "プレビに失敗しました: " + (error.message || "サーバーエラー"),
      );
    } finally {
    }
  };

  const handlePublish = async () => {
    if (!window.confirm("バナーを公開しますか？")) return;

    setIsPublishing(true);

    try {
      const htmlContent = generateBannerHtml(banners);

      await publishBannerHtml({
        html: htmlContent,
        banners: banners,
      });

      toast.success("バナーを公開しました！");
    } catch (error) {
      console.error("Publish failed:", error);
      toast.error("公開に失敗しました: " + (error.message || "サーバーエラー"));
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDownload = () => {
    const html = generateBannerHtml(banners);
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "banners_new.html";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success("HTMLファイルをダウンロードしました");
  };

  const getImageFileName = (src) => {
    if (!src) return "";
    if (src.startsWith("http")) {
      return src.split("/").pop();
    }
    return src.replace("pic/", "");
  };

  if (isLoading) {
    return (
      <MainLayout>
        <BannerManageLayout>
          <BannerManageBody>
            <div style={{ textAlign: "center", padding: "50px" }}>
              <p>読み込み中...</p>
            </div>
          </BannerManageBody>
        </BannerManageLayout>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <BannerManageLayout>
        <BannerManageBody>
          <Title>バナー管理</Title>

          <StyledCard>
            <CardHeader>バナー設定</CardHeader>
            <CardBody>
              <FlexRow
                style={{
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <SmallButton variant="info" onClick={loadBannersFromServer}>
                    リフレッシュ
                  </SmallButton>
                  <TextMuted>自動読み込み済み ({banners.length}件)</TextMuted>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <SmallButton
                    variant="info"
                    onClick={previewForPublish}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FaEye /> プレビュー
                  </SmallButton>
                </div>
              </FlexRow>
            </CardBody>
          </StyledCard>

          <StyledCard>
            <CardHeader>
              <span>バナー一覧 ({banners.length}件)</span>
              <SmallButton
                variant="primary"
                onClick={addBanner}
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <FaPlus /> バナー追加
              </SmallButton>
            </CardHeader>
            <CardBody>
              <TableWrapper>
                <StyledTable>
                  <thead>
                    <tr>
                      <th style={{ width: "60px" }}>順序</th>
                      <th style={{ width: "140px" }}>ID</th>
                      <th style={{ width: "110px" }}>画像</th>
                      <th>画像ファイル名</th>
                      <th>遷移先URL</th>
                      <th>広告名(Alt)</th>
                      <th style={{ width: "70px" }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {banners.map((banner, index) => (
                      <tr key={index}>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: "2px",
                            }}
                          >
                            <SmallButton
                              variant="link"
                              onClick={() => moveBanner(index, "up")}
                              disabled={index === 0}
                              style={{ padding: "2px 5px", margin: 0 }}
                            >
                              <FaArrowUp style={{ fontSize: "10px" }} />
                            </SmallButton>
                            <SmallButton
                              variant="link"
                              onClick={() => moveBanner(index, "down")}
                              disabled={index === banners.length - 1}
                              style={{ padding: "2px 5px", margin: 0 }}
                            >
                              <FaArrowDown style={{ fontSize: "10px" }} />
                            </SmallButton>
                          </div>
                        </td>
                        <td>
                          <FormControl
                            type="text"
                            value={banner.id}
                            onChange={(e) =>
                              updateBanner(index, "id", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <PreviewImage>
                            {uploadedImages[index] ? (
                              uploadedImages[index].fullUrl ? (
                                <img
                                  src={uploadedImages[index].fullUrl}
                                  alt="preview"
                                />
                              ) : (
                                <FaImage
                                  style={{ color: "#4CAF50", fontSize: "18px" }}
                                  title="アップロード済み"
                                />
                              )
                            ) : banner.src ? (
                              <img
                                src={banner.src}
                                alt="preview"
                                onError={(e) => {
                                  // e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <FaImage
                                style={{ color: "#ccc", fontSize: "18px" }}
                              />
                            )}
                          </PreviewImage>
                          <UploadButton
                            onClick={() => openUploadModal(index)}
                            disabled={isUploading}
                          >
                            {isUploading ? "..." : "アップロード"}
                          </UploadButton>
                        </td>
                        <td>
                          <FormControl
                            type="text"
                            value={getImageFileName(banner.src)}
                            onChange={(e) =>
                              updateBanner(
                                index,
                                "src",
                                `pic/${e.target.value}`,
                              )
                            }
                            placeholder="画像ファイル名"
                          />
                        </td>
                        <td>
                          <FormControl
                            type="text"
                            value={banner.href}
                            onChange={(e) =>
                              updateBanner(index, "href", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <FormControl
                            type="text"
                            value={banner.alt}
                            onChange={(e) =>
                              updateBanner(index, "alt", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <SmallButton
                            variant="danger"
                            onClick={() => removeBanner(index)}
                            disabled={banners.length <= 1}
                          >
                            <FaTrash />
                          </SmallButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </StyledTable>
              </TableWrapper>
            </CardBody>
            <CardHeader>
              <span>バナー一覧 ({banners.length}件)</span>
              <SmallButton
                variant="primary"
                onClick={addBanner}
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <FaPlus /> バナー追加
              </SmallButton>
            </CardHeader>
          </StyledCard>

          <div
            style={{
              textAlign: "center",
              margin: "20px 0",
              display: "flex",
              gap: "15px",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <SmallButton
              variant="outline-primary"
              onClick={handleDownload}
              style={{
                padding: "12px 25px",
                fontSize: "16px",
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <FaDownload />
              HTMLダウンロード
            </SmallButton>

            <SmallButton
              variant="success"
              onClick={handlePublish}
              disabled={isPublishing}
              style={{
                padding: "12px 30px",
                fontSize: "16px",
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                backgroundColor: "#28a745",
              }}
            >
              <FaCloudUploadAlt />
              {isPublishing ? "公開中..." : "公開する"}
            </SmallButton>
          </div>

          <StyledCard>
            <CardHeader>使い方</CardHeader>
            <CardBody>
              <Instructions>
                <li>
                  ページを開くと自動的にサーバからbanners.htmlを読み込みます
                </li>
                <li>
                  「アップロード」ボタンから画像をサーバーにアップロード（ファイル名を一時自動生成）
                </li>
                <li>▲▼ボタンでバナーの順序を変更できます</li>
                <li>画像ファイル名とリンクURLを編集</li>
                <li>「プレビュー」ボタンをクリックして確認</li>
                <li>
                  問題がなければ「公開する」ボタンをクリックしてサーバーに反映
                </li>
              </Instructions>
            </CardBody>
          </StyledCard>
        </BannerManageBody>
      </BannerManageLayout>

      {showUploadModal && (
        <ModalOverlay onClick={() => setShowUploadModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h5 style={{ marginBottom: "10px" }}>画像アップロード</h5>
            <p
              style={{
                color: "#888787",
                marginBottom: "15px",
                fontSize: "14px",
              }}
            >
              バナー #{currentEditingIndex + 1} の画像をアップロード
            </p>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              ref={fileInputRef}
              style={{
                padding: "8px",
                border: "1px solid #888787",
                borderRadius: "4px",
                width: "100%",
              }}
            />
            {isUploading && (
              <p style={{ color: "#0095FF", marginTop: "10px" }}>
                アップロード中...
              </p>
            )}
            <div style={{ marginTop: "15px", textAlign: "right" }}>
              <SmallButton
                variant="secondary"
                onClick={() => setShowUploadModal(false)}
                disabled={isUploading}
              >
                キャンセル
              </SmallButton>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </MainLayout>
  );
}
